import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Phone, Mail, Users, MapPin, Cake } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

// Erweiterte Schnittstelle mit allen Feldern für Mitarbeiter
interface DirectoryEmployee {
  id: string;
  name: string;
  position: string | null;
  department: string | null;
  business_email: string | null;
  business_phone: string | null;
  mobile: string | null;
  private_phone: string | null;
  private_email: string | null;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  birth_date: string | null;
  profile_image_url: string | null;
}

export function EmployeeDirectory() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<DirectoryEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user) {
      loadEmployees();
    }
  }, [user]);

  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase.rpc("get_employee_directory");
      
      if (error) throw error;
      
      // Map prefixed column names to interface properties
      const mappedData: DirectoryEmployee[] = (data || []).map((row: {
        emp_id: string;
        emp_name: string;
        emp_position: string | null;
        emp_department: string | null;
        emp_business_email: string | null;
        emp_business_phone: string | null;
        emp_mobile: string | null;
        emp_private_phone: string | null;
        emp_private_email: string | null;
        emp_address_street: string | null;
        emp_address_zip: string | null;
        emp_address_city: string | null;
        emp_birth_date: string | null;
        emp_profile_image_url: string | null;
      }) => ({
        id: row.emp_id,
        name: row.emp_name,
        position: row.emp_position,
        department: row.emp_department,
        business_email: row.emp_business_email,
        business_phone: row.emp_business_phone,
        mobile: row.emp_mobile,
        private_phone: row.emp_private_phone,
        private_email: row.emp_private_email,
        address_street: row.emp_address_street,
        address_zip: row.emp_address_zip,
        address_city: row.emp_address_city,
        birth_date: row.emp_birth_date,
        profile_image_url: row.emp_profile_image_url,
      }));
      
      setEmployees(mappedData);
    } catch (error) {
      console.error("Error loading employee directory:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    const query = searchQuery.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(query) ||
      emp.position?.toLowerCase().includes(query) ||
      emp.department?.toLowerCase().includes(query) ||
      emp.address_city?.toLowerCase().includes(query)
    );
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatAddress = (emp: DirectoryEmployee) => {
    const parts = [];
    if (emp.address_street) parts.push(emp.address_street);
    if (emp.address_zip || emp.address_city) {
      parts.push([emp.address_zip, emp.address_city].filter(Boolean).join(" "));
    }
    return parts.join(", ");
  };

  const formatBirthDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      return format(new Date(dateStr), "d. MMMM yyyy", { locale: de });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Kollegenverzeichnis</CardTitle>
              <CardDescription>Kontaktdaten aller Kollegen</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Nach Name, Position, Abteilung oder Ort suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {filteredEmployees.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {searchQuery ? "Keine Mitarbeiter gefunden" : "Keine Mitarbeiter vorhanden"}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredEmployees.map((emp) => (
                <Card key={emp.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-14 w-14 flex-shrink-0">
                        <AvatarImage src={emp.profile_image_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary font-medium">
                          {getInitials(emp.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div>
                          <h3 className="font-semibold text-foreground truncate">{emp.name}</h3>
                          {emp.position && (
                            <p className="text-sm text-muted-foreground truncate">{emp.position}</p>
                          )}
                          {emp.department && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {emp.department}
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-1.5 text-sm">
                          {/* Anschrift */}
                          {formatAddress(emp) && (
                            <div className="flex items-start gap-2 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                              <span className="text-foreground/80">{formatAddress(emp)}</span>
                            </div>
                          )}

                          {/* Geburtsdatum */}
                          {emp.birth_date && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Cake className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="text-foreground/80">{formatBirthDate(emp.birth_date)}</span>
                            </div>
                          )}

                          {/* Geschäftliche Telefonnummer */}
                          {emp.business_phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                              <a 
                                href={`tel:${emp.business_phone}`}
                                className="truncate hover:text-primary transition-colors"
                              >
                                {emp.business_phone}
                              </a>
                              <span className="text-xs text-muted-foreground/60">(Geschäft)</span>
                            </div>
                          )}
                          
                          {/* Mobil */}
                          {emp.mobile && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                              <a 
                                href={`tel:${emp.mobile}`}
                                className="truncate hover:text-primary transition-colors"
                              >
                                {emp.mobile}
                              </a>
                              <span className="text-xs text-muted-foreground/60">(Mobil)</span>
                            </div>
                          )}

                          {/* Private Telefonnummer */}
                          {emp.private_phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                              <a 
                                href={`tel:${emp.private_phone}`}
                                className="truncate hover:text-primary transition-colors"
                              >
                                {emp.private_phone}
                              </a>
                              <span className="text-xs text-muted-foreground/60">(Privat)</span>
                            </div>
                          )}
                          
                          {/* Geschäftliche E-Mail */}
                          {emp.business_email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                              <a 
                                href={`mailto:${emp.business_email}`}
                                className="truncate hover:text-primary transition-colors"
                              >
                                {emp.business_email}
                              </a>
                              <span className="text-xs text-muted-foreground/60">(Geschäft)</span>
                            </div>
                          )}

                          {/* Private E-Mail */}
                          {emp.private_email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                              <a 
                                href={`mailto:${emp.private_email}`}
                                className="truncate hover:text-primary transition-colors"
                              >
                                {emp.private_email}
                              </a>
                              <span className="text-xs text-muted-foreground/60">(Privat)</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default EmployeeDirectory;
